import { useMutation } from '@apollo/client';
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import type { PaymentMethodResult } from '@stripe/stripe-js';
import { Stack, Columns, Column, Button } from 'braid-design-system';
import debounce from 'lodash/debounce';
import queryString from 'query-string';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import CustomLoader from 'src/components/CustomLoader/CustomLoader';
import { Error as GenericError } from 'src/components/Error/Error';
import Routes from 'src/config/routes';
import {
  type ListingProduct,
  ListingProductCode,
  PurchaseAndActivateDocument,
} from 'src/generated/graphql';
import type { ProductBundle } from 'src/types/productBundle';
import { ConsoleErrorId, consoleErrorWrapper } from 'src/utils';
import { firePaymentSuccessEvent } from 'src/utils/analytics';
import { clearSelectedProduct } from 'src/utils/cookies';
import {
  BOOST_BUNDLE_DISCOUNT_FEATURE_TOGGLE_ENABLED,
  getBoostBundlePrices,
} from 'src/utils/feature-flags';

import AddOns from './components/AddOns/AddOns';
import {
  CheckoutFormError,
  type CheckoutFormErrorProps,
} from './components/CheckoutFormField/CheckoutFormError';
import CheckoutFormField from './components/CheckoutFormField/CheckoutFormField';

interface CheckoutFormProps {
  isListingLus: boolean;
  isBundleProduct: boolean;
  client: { id: string };
  productBundle: ProductBundle;
  selectedProducts: ListingProduct[];
  searchBoostChecked: boolean;
  onSearchBoostToggle: () => void;
}

const CheckoutForm = ({
  isListingLus,
  isBundleProduct,
  client,
  productBundle,
  selectedProducts,
  searchBoostChecked,
  onSearchBoostToggle,
}: CheckoutFormProps) => {
  const dispatch = useDispatch();

  const [validCvc, setValidCvc] = useState(false);
  const [validCardNumber, setValidCardNumber] = useState(false);
  const [validCardExpiry, setValidCardExpiry] = useState(false);
  const [hasUnknownSubmissionError, setHasUnknownSubmissionError] =
    useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const stripe = useStripe();
  const elements = useElements();

  const [purchaseProduct, purchaseProductResult] = useMutation(
    PurchaseAndActivateDocument,
  );

  const { listingId } = queryString.parseUrl(location.search).query;
  // "listingId" is optional. If it's present it should be a string not an array
  const isListingIdValidType =
    listingId === undefined ||
    listingId === null ||
    typeof listingId === 'string';
  if (!isListingIdValidType) {
    return <GenericError />;
  }

  let displayCost;
  if (BOOST_BUNDLE_DISCOUNT_FEATURE_TOGGLE_ENABLED && isBundleProduct) {
    displayCost = getBoostBundlePrices({ isLus: isListingLus }).discount;
  } else {
    displayCost =
      productBundle.availableSearchBoost &&
      (searchBoostChecked ||
        productBundle.info.productCode.includes(ListingProductCode.BoostedAd))
        ? productBundle.availableSearchBoost.cost
        : productBundle.info.cost;
  }

  const handleUnknownSubmissionError = (errorMessageId: ConsoleErrorId) => {
    consoleErrorWrapper(errorMessageId, 'Unknown payment submission error');
    setHasUnknownSubmissionError(true);
  };

  const submit = debounce(async () => {
    // Reset error tracking state
    setHasUnknownSubmissionError(false);
    const card = elements?.getElement(CardNumberElement);

    if (
      !elements ||
      !card ||
      !stripe ||
      !validCardExpiry ||
      !validCardNumber ||
      !validCvc
    ) {
      return;
    }

    let result: PaymentMethodResult;
    try {
      result = await stripe.createPaymentMethod({
        type: 'card',
        card,
      });
    } catch {
      handleUnknownSubmissionError(ConsoleErrorId.StripeCreatePaymentMethod);
      return;
    }

    if (!result.paymentMethod) {
      handleUnknownSubmissionError(ConsoleErrorId.StripeMalformedPaymentMethod);
      return;
    }

    let product;
    let selectedProductsClone;
    if (searchBoostChecked && productBundle.availableSearchBoost) {
      product = productBundle.availableSearchBoost;
      selectedProductsClone = [productBundle.availableSearchBoost];
    } else {
      product = productBundle.info;
      selectedProductsClone = [...selectedProducts];
    }
    // !product.info and !product.search is already caught
    // to produce a Generic error
    if (!product) {
      handleUnknownSubmissionError(ConsoleErrorId.PaymentFailedToFindProduct);
      return;
    }

    const hasEmailBoost = !productBundle.availableEmailBoost;
    // This is quite fragile. Ideally we'd enforce in the parent that, if email
    // boost is chosen, the "listingId" also exists. I would look to enforce
    // this via the compiler
    if (hasEmailBoost && typeof listingId !== 'string') {
      handleUnknownSubmissionError(
        ConsoleErrorId.PaymentEmailBoostMissingListing,
      );
      return;
    }

    try {
      await purchaseProduct({
        variables: {
          input: {
            clientId: client.id,
            paymentMethodId: result.paymentMethod.id,
            productId: product.productToken,
            listingId: listingId?.toString(),
          },
        },
      });
    } catch {
      // Let purchaseProductResult.error handle rendering our error message
      return;
    }

    clearSelectedProduct();
    firePaymentSuccessEvent(selectedProductsClone);
    dispatch({ type: 'REQUEST_PAYMENT_SUCCESS' });

    navigate(
      queryString.stringifyUrl({
        url: Routes.PAYMENT_SUCCESS,
        query: {
          productToken: selectedProductsClone.map((x) => x.productToken),
          listingId: listingId?.toString(),
        },
      }),
    );
  }, 500);

  let error: CheckoutFormErrorProps['error'] | null = null;
  if (hasUnknownSubmissionError || purchaseProductResult.error?.networkError) {
    error = { type: 'unexpected' };
  } else if (purchaseProductResult.error) {
    error = { type: 'expected', message: purchaseProductResult.error.message };
  }

  return (
    <Stack space="medium">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        {purchaseProductResult.loading ? (
          <CustomLoader />
        ) : (
          <Stack space="large">
            <Stack space="small">
              <CheckoutFormField label="Card Number" id="cardNumber">
                <CardNumberElement
                  onChange={(ev) => setValidCardNumber(ev.complete)}
                />
              </CheckoutFormField>
              <Columns space="small" collapseBelow="desktop">
                <Column>
                  <CheckoutFormField label="CVC" id="cvc">
                    <CardCvcElement
                      onChange={(ev) => setValidCvc(ev.complete)}
                    />
                  </CheckoutFormField>
                </Column>
                <Column>
                  <CheckoutFormField label="Card Expiry" id="cardExpiry">
                    <CardExpiryElement
                      onChange={(ev) => setValidCardExpiry(ev.complete)}
                    />
                  </CheckoutFormField>
                </Column>
              </Columns>
            </Stack>

            {productBundle.info.productCode !== ListingProductCode.Accelerate &&
              productBundle.availableSearchBoost && (
                <AddOns
                  isListingLus={isListingLus}
                  productBundle={productBundle}
                  searchBoostChecked={searchBoostChecked}
                  onSearchBoostToggle={onSearchBoostToggle}
                />
              )}

            <Button type="submit">Pay ${displayCost / 100} + GST</Button>
          </Stack>
        )}
      </form>

      {error && <CheckoutFormError error={error} />}
    </Stack>
  );
};

export default CheckoutForm;
